import { z } from 'zod';
import { CHAINS } from './chains.js';

const chainSchema = z.enum(CHAINS);
const microsSchema = z.string().regex(/^\d+$/, 'must be an integer string of micro-units');

/** Base58, 32-byte Solana pubkey. EVM addresses validated separately per chain. */
const solanaAddress = z.string().regex(/^[1-9A-HJ-NP-Za-km-z]{32,44}$/, 'invalid Solana address');
const evmAddress = z.string().regex(/^0x[a-fA-F0-9]{40}$/, 'invalid EVM address');

export const addressForChain = (chain: string) =>
  chain === 'solana' ? solanaAddress : evmAddress;

// ---------------------------------------------------------------- auth

export const nonceRequest = z.object({
  chain: chainSchema,
  address: z.string().min(32).max(64),
});

export const verifyRequest = z.object({
  chain: chainSchema,
  address: z.string().min(32).max(64),
  /** The full SIWS/SIWE statement that was presented to the user. */
  message: z.string().min(1).max(2000),
  /** base58 (Solana) or hex (EVM) signature over `message`. */
  signature: z.string().min(1).max(400),
});

// ---------------------------------------------------------------- profile

export const updateProfileRequest = z.object({
  displayName: z.string().trim().min(1).max(40).nullable().optional(),
  bio: z.string().trim().max(280).nullable().optional(),
  avatarUrl: z.string().url().max(500).nullable().optional(),
  inboxPriceMicros: microsSchema.optional(),
  replyWindowHours: z.number().int().min(1).max(720).optional(),
  inboxPaused: z.boolean().optional(),
  /** Collection whose holders skip the fee. Empty string clears the gate. */
  freeForCollection: z.string().trim().max(120).nullable().optional(),
});

// ---------------------------------------------------------------- messaging

export const sendMessageRequest = z.object({
  toChain: chainSchema,
  toAddress: z.string().min(32).max(64),
  body: z.string().trim().min(1).max(4000),
  /**
   * Required when the recipient's inbox is priced. The client funds the escrow
   * on-chain first and passes the signature; the server verifies it before the
   * message becomes visible.
   */
  escrow: z
    .object({
      amountMicros: microsSchema,
      replyWindowHours: z.number().int().min(1).max(720),
      fundingTxSignature: z.string().min(1).max(200),
      vaultAddress: z.string().min(1).max(200),
    })
    .optional(),
});

export const replyRequest = z.object({
  body: z.string().trim().min(1).max(4000),
});

export const listThreadsQuery = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  filter: z.enum(['all', 'paid', 'unread', 'awaiting-reply']).default('all'),
});

// ---------------------------------------------------------------- notifications

export const linkChannelRequest = z.object({
  channel: z.enum(['telegram', 'discord', 'email']),
});

export const notificationPrefsRequest = z.object({
  onNewMessage: z.boolean().optional(),
  onReply: z.boolean().optional(),
  onEscrowExpiring: z.boolean().optional(),
  /** Suppress notifications for unpaid messages entirely. */
  paidOnly: z.boolean().optional(),
});

export type NonceRequest = z.infer<typeof nonceRequest>;
export type VerifyRequest = z.infer<typeof verifyRequest>;
export type UpdateProfileRequest = z.infer<typeof updateProfileRequest>;
export type SendMessageRequest = z.infer<typeof sendMessageRequest>;
export type ReplyRequest = z.infer<typeof replyRequest>;
export type ListThreadsQuery = z.infer<typeof listThreadsQuery>;
export type LinkChannelRequest = z.infer<typeof linkChannelRequest>;
export type NotificationPrefsRequest = z.infer<typeof notificationPrefsRequest>;
